import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { memorialThemes } from '../../styles/themes';
import VisualIdentityTab from './VisualIdentityTab';

const { FiCheck, FiLayout, FiMaximize2, FiEdit3, FiDroplet, FiType } = FiIcons;

const ThemeSelector = ({ memorial, currentTheme, onThemeChange, onSave, coverImage, profileImage, setCoverImage, setProfileImage }) => {
  const [activeTab, setActiveTab] = useState('presets'); // 'presets', 'custom', or 'visual_identity'
  const [draftTheme, setDraftTheme] = useState(currentTheme);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const handleCustomColor = (type, color) => {
    const updatedTheme = { 
      ...draftTheme, 
      id: 'custom', 
      name: 'Custom Legacy', 
      isCustom: true, 
      [type]: color 
    };
    setDraftTheme(updatedTheme);
    onThemeChange(updatedTheme); // Preview live
  };

  const handlePresetSelect = (theme) => {
    setDraftTheme(theme);
    onThemeChange(theme); // Preview live
  };

  const handleSave = () => {
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      setSaveSuccess(true);
      if (onSave) {
        onSave(draftTheme);
      }
      setTimeout(() => setSaveSuccess(false), 3000);
    }, 1000);
  };

  return (
    <div className="bg-white p-8 rounded-[40px] border border-brand-dark shadow-sm">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-brand-light flex items-center justify-center text-accent-gold shadow-sm">
            <SafeIcon icon={FiLayout} className="w-6 h-6" />
          </div>
          <div>
            <h3 className="font-serif text-2xl text-accent-navy">Visual Identity</h3>
            <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Customize the memorial sanctuary</p>
          </div>
        </div>
        <button 
          onClick={handleSave}
          disabled={isSaving || saveSuccess}
          className={`btn-primary py-3 px-8 text-xs flex items-center justify-center gap-2 min-w-[140px] ${saveSuccess ? 'bg-green-500' : ''}`}
        >
          {isSaving ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : saveSuccess ? (
            <><SafeIcon icon={FiCheck} /> Saved</>
          ) : (
            'Save Settings'
          )}
        </button>
      </div>

      <div className="flex gap-2 mb-8 bg-brand-light p-1.5 rounded-2xl border border-brand-dark">
        <button 
          onClick={() => setActiveTab('presets')}
          className={`flex-1 py-3 px-4 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'presets' ? 'bg-white text-accent-navy shadow-sm' : 'text-text-muted hover:text-accent-navy'}`}
        >
          Presets
        </button>
        <button 
          onClick={() => setActiveTab('custom')}
          className={`flex-1 py-3 px-4 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'custom' ? 'bg-white text-accent-navy shadow-sm' : 'text-text-muted hover:text-accent-navy'}`}
        >
          Custom
        </button>
        <button 
          onClick={() => setActiveTab('visual_identity')}
          className={`flex-1 py-3 px-4 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'visual_identity' ? 'bg-white text-accent-navy shadow-sm' : 'text-text-muted hover:text-accent-navy'}`}
        >
          Banner
        </button>
      </div>

      <AnimatePresence mode="wait">
        {activeTab === 'presets' ? (
          <motion.div 
            key="presets"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="grid grid-cols-1 gap-4"
          >
            {Object.values(memorialThemes).filter(t => !t.isCustom).map((theme) => (
              <button 
                key={theme.id}
                onClick={() => handlePresetSelect(theme)}
                className={`flex items-center justify-between p-5 rounded-3xl border-2 transition-all group ${
                  draftTheme.id === theme.id 
                    ? 'border-accent-gold bg-brand-light' 
                    : 'border-brand-dark hover:border-accent-gold/50 bg-white'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className="flex -space-x-2">
                    <div className="w-8 h-8 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: theme.primary }} />
                    <div className="w-8 h-8 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: theme.accent }} />
                  </div>
                  <div className="text-left">
                    <span className={`block font-bold text-sm ${draftTheme.id === theme.id ? 'text-accent-navy' : 'text-text-muted'}`}>
                      {theme.name}
                    </span>
                    <span className="text-[9px] font-black uppercase tracking-tighter text-text-muted opacity-60">
                      {theme.font.split('-')[1]} • {theme.id}
                    </span>
                  </div>
                </div>
                {draftTheme.id === theme.id && (
                  <div className="w-6 h-6 rounded-full bg-accent-gold flex items-center justify-center text-white shadow-lg">
                    <SafeIcon icon={FiCheck} className="w-3.5 h-3.5" />
                  </div>
                )}
              </button>
            ))}
          </motion.div>
        ) : activeTab === 'custom' ? (
          <motion.div 
            key="custom"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-8"
          >
            <div className="space-y-6">
              <div className="space-y-4">
                <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy flex items-center gap-2">
                  <SafeIcon icon={FiDroplet} className="text-accent-gold" /> Primary Legacy Color
                </label>
                <div className="flex items-center gap-4">
                  <input 
                    type="color" 
                    value={draftTheme.primary}
                    onChange={(e) => handleCustomColor('primary', e.target.value)}
                    className="w-14 h-14 rounded-2xl cursor-pointer border-4 border-white shadow-xl"
                  />
                  <input 
                    type="text" 
                    value={draftTheme.primary}
                    onChange={(e) => handleCustomColor('primary', e.target.value)}
                    className="flex-grow bg-brand-light border-2 border-brand-dark rounded-2xl px-6 py-4 font-mono text-sm uppercase outline-none focus:border-accent-gold"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy flex items-center gap-2">
                  <SafeIcon icon={FiDroplet} className="text-accent-gold" /> Accent Color
                </label>
                <div className="flex items-center gap-4">
                  <input 
                    type="color" 
                    value={draftTheme.accent}
                    onChange={(e) => handleCustomColor('accent', e.target.value)}
                    className="w-14 h-14 rounded-2xl cursor-pointer border-4 border-white shadow-xl"
                  />
                  <input 
                    type="text" 
                    value={draftTheme.accent}
                    onChange={(e) => handleCustomColor('accent', e.target.value)}
                    className="flex-grow bg-brand-light border-2 border-brand-dark rounded-2xl px-6 py-4 font-mono text-sm uppercase outline-none focus:border-accent-gold"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy flex items-center gap-2">
                  <SafeIcon icon={FiType} className="text-accent-gold" /> Typography Style
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {['font-serif', 'font-sans'].map(f => (
                    <button
                      key={f}
                      onClick={() => handleCustomColor('font', f)}
                      className={`py-4 rounded-2xl border-2 font-bold text-xs uppercase tracking-widest transition-all ${
                        draftTheme.font === f 
                          ? 'bg-accent-navy border-accent-navy text-white shadow-lg' 
                          : 'bg-white border-brand-dark text-text-muted hover:border-accent-gold'
                      }`}
                    >
                      {f.split('-')[1]}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div 
            key="visual_identity"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <VisualIdentityTab 
              memorial={memorial}
              coverImage={coverImage}
              profileImage={profileImage}
              setCoverImage={setCoverImage}
              setProfileImage={setProfileImage}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-10 p-6 bg-brand-light rounded-[32px] border border-brand-dark flex items-center gap-4">
        <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-accent-gold shadow-sm">
          <SafeIcon icon={FiEdit3} className="w-5 h-5" />
        </div>
        <p className="text-xs text-text-muted leading-relaxed font-light">
          Your visual choices reflect the personality of your loved one. Click "Save Settings" to apply changes globally.
        </p>
      </div>
    </div>
  );
};

export default ThemeSelector;